import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  TrademarksImportOverviewListDialogComponent,
} from 'src/app/dialogs/trademarks-import-overview-list-dialog/trademarks-import-overview-list-dialog.component';
import {
  TrademarkDTO,
  TrademarkDTOSearch,
  TrademarkStatus,
} from 'src/app/models/trademark';
import { TrademarkFamily } from 'src/app/models/trademarkFamily';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import { TerritoryService } from 'src/app/services/territory.service';
import { MatPaginator } from '@angular/material/paginator';
// @ts-ignore
import * as ExcelJS from 'exceljs';
import { AclService } from '../../../../services/acl.service';
import { environment } from 'src/environments/environment';
import {ExcelService} from '../../../../services/excel.service';

export interface TrademarkErrors {
  missingIdentifier: ITrademarkImportLine[],
  missingCountry: ITrademarkImportLine[],
  invalidCountry: ITrademarkImportLine[],
  missingBrandType: ITrademarkImportLine[],
  missingOffice: ITrademarkImportLine[],
  invalidFamily: ITrademarkImportLine[],
  unprocessable: ITrademarkImportLine[],
  search: ITrademarkImportLine[],
  notFound: ITrademarkImportLine[]
}

export enum ETrademarkImportColumn {
  TRADEMARK_NAME = "TRADEMARK_NAME",
  APPLICATION_NUMBER = "APPLICATION_NUMBER",
  REGISTRATION_NUMBER = "REGISTRATION_NUMBER",
  APPLICATION_DATE = "APPLICATION_DATE",
  PUBLICATION_DATE = "PUBLICATION_DATE",
  REGISTRATION_DATE = "REGISTRATION_DATE",
  COUNTRY_OF_DESIGNATION = "COUNTRY_OF_DESIGNATION",
  BRAND_TYPE = "BRAND_TYPE",
  OFFICE = "OFFICE",
  REPRODUCTION = "REPRODUCTION",
  NICE_CLASSIFICATION = "NICE_CLASSIFICATION",
  STATUS = "STATUS",
  PRODUCTS_SERVICES = "PRODUCTS_SERVICES",
  CLIENT_REFERENCE = "CLIENT_REFERENCE",
  DESCRIPTION = "DESCRIPTION"
}

type TrademarkImportLineColumns = {
  [K in ETrademarkImportColumn]: string;
};

export type ITrademarkImportLine = {
  line: number;
} & Partial<TrademarkImportLineColumns>;

export interface ITrademarkImportColumns {
  [ETrademarkImportColumn.TRADEMARK_NAME]: string,
  [ETrademarkImportColumn.APPLICATION_NUMBER]: string,
  [ETrademarkImportColumn.REGISTRATION_NUMBER]: string,
  [ETrademarkImportColumn.APPLICATION_DATE]: string,
  [ETrademarkImportColumn.PUBLICATION_DATE]: string,
  [ETrademarkImportColumn.REGISTRATION_DATE]: string,
  [ETrademarkImportColumn.COUNTRY_OF_DESIGNATION]: string,
  [ETrademarkImportColumn.BRAND_TYPE]: string,
  [ETrademarkImportColumn.OFFICE]: string,
  [ETrademarkImportColumn.REPRODUCTION]: string,
  [ETrademarkImportColumn.NICE_CLASSIFICATION]: string,
  [ETrademarkImportColumn.STATUS]: string,
  [ETrademarkImportColumn.PRODUCTS_SERVICES]: string,
  [ETrademarkImportColumn.CLIENT_REFERENCE]: string,
  [ETrademarkImportColumn.DESCRIPTION]: string,
}

export interface TrademarkImportSheetInfo {
  value: string,
  label: string,
  selected: boolean,
  rowsCount: number
}

export interface ITrademarkImport {
  isExcel: boolean;
  prepared: boolean;
  parsed: boolean;
  delimiter: string;
  sheets: TrademarkImportSheetInfo[];
  sheet?: string;
  firstLine?: number;
  columns: ITrademarkImportColumns,
  trademarks: ITrademarkImportLine[],
  familyModes: { value: string, label: string, selected: boolean }[],
  familyMode?: 'auto' | 'create' | 'select' | 'column';
  familyColumn?: string;
  family?: TrademarkFamily;
  errors: TrademarkErrors
}

/**
 * Convert an ArrayBuffer to a Base64 string (Browser)
 * @param {ArrayBuffer} buffer - The binary data to encode
 * @returns {string} Base64 encoded string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCodePoint(b);
  }
  return btoa(binary);
}

@Component({
  selector: 'app-trademark-declare-import',
  templateUrl: './trademark-declare-import.component.html',
  styleUrls: ['./trademark-declare-import.component.scss']
})
export class TrademarkDeclareImportComponent implements OnInit, AfterViewInit {
  public columnsArr: (keyof typeof ETrademarkImportColumn)[] = Object.keys(ETrademarkImportColumn) as (keyof typeof ETrademarkImportColumn)[];

  public active: boolean = false;
  public loading: boolean = false;
  public started: boolean = true;
  public done: boolean = false;
  public progress: number = 0;
  public timeLeft?: number;
  private readonly accepted: string[] = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];

  file?: File;

  wipoCodes: string[] = ["wo"];

  import: ITrademarkImport = this.initImport();

  displayedColumns: string[] = ['select', 'name', 'identifierNumber', 'countryOfDesignation', 'niceClassification', 'status', 'representation'];
  public trademarks: TrademarkDTO[] = [];
  public trademarksWithoutDesignations: TrademarkDTO[] = [];
  public trademarksToDeclare: TrademarkDTO[] = [];
  dataSource: MatTableDataSource<TrademarkDTO> = new MatTableDataSource<TrademarkDTO>([])
  public selection = new SelectionModel<TrademarkDTO>(true, []);
  // Selection state is recomputed only when the selection or the rows change: the template reads it
  // on every change detection, and rebuilding it there costs rows x selected rows (10^8 for 10,000 rows).
  private selectedKeys = new Set<string>();
  private allSelected = true;
  private someSelected = false;

  @ViewChild('paginator')
  set paginator(p: MatPaginator | undefined) {
    if (p) {
      this.dataSource.paginator = p;
      p.pageSize = 50; // default page size
    }
  }

  constructor(
    private readonly _message: MessageService,
    private readonly dialog: MatDialog,
    private readonly documentService: DocumentService,
    private readonly territoryService: TerritoryService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly aclService: AclService,
    private readonly excelService: ExcelService
  ) {
    this.selection.changed.subscribe(() => this.refreshSelectionState());
  }

  ngOnInit(): void {
    this.retrieveCountries();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator ?? null;
  }

  /**
   * Retrieve countries from territory service, update wipoCodes list
   */
  async retrieveCountries() {
    const territories = await this.territoryService.retrieve();
    territories.forEach((territory) => {
      this.wipoCodes.push(territory.id.toLowerCase());
      if (territory.alternateCodes) {
        this.wipoCodes.push(...territory.alternateCodes.map(code => code.toLowerCase()))
      }
    })
  }

  @HostListener('dragover', ['$event']) onDragOver(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.active = true;
  }

  // Dragleave listener
  @HostListener('dragleave', ['$event']) public onDragLeave(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.active = false;
  }

  // Drop listener
  @HostListener('drop', ['$event']) public onDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.active = false;
    const file = event.dataTransfer.files[0];
    this.handleFile(file);
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    this.handleFile(file);
  }

  handleFile(file: File) {
    this.file = file;
    if (["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].includes(file.type)) {
      this.import.isExcel = true;
      this._prepareFile(file)
    } else if (file.type === "text/csv") {
      this.import.isExcel = false;
      this._prepareFile(file);
    } else {
      this.toastr.warning("TRADEMARK_DECLARE.UNAUTHORIZED_FORMAT_MESSAGE");
      this.removeFile();
    }
  }

  removeFile() {
    this.file = undefined;
    this.import = this.initImport();
  }

  /**
   * Read excel/csv file and prepare sheets list for selection
   */
  async _prepareFile(file: File) {
    try {
      this.startLoading();
      this.prepareImport();
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      if (!this.import.sheet) {
        this.import.sheets = workbook.worksheets.map(
          (sheet, index) => ({
            selected: index === 0,
            label: sheet.name,
            value: sheet.name,
            rowsCount: sheet.actualRowCount,
          }));
        this.import.sheet = this.import.sheets[0]?.value;
      }

      this.import.prepared = this.checkTotalRowCountIsBelowThreshold();
    } catch (err) {
      this.toastr.error('ERRORS.GENERIC');
      console.error(err);
    } finally {
      this.endLoading();
    }
  }

  parseFileDisabled() {
    return this.import.firstLine === undefined
      || (this.import.isExcel && !this.import.sheet) || (!this.import.isExcel && !this.import.delimiter)
      || (this.import.familyMode && ['create', 'select'].includes(this.import.familyMode) && !this.import.family)
      || (this.import.familyMode === 'column' && !this.import.familyColumn);
  }

  // Check whether import excel file exceeds row import threshold (100)
  private checkTotalRowCountIsBelowThreshold(): boolean {
    const { excelRows: threshold } = environment.thresholds.imports;
    let isBelowThreshold = true;

    // dont check files uploaded by admins
    if (this.aclService.isAdmin() || this.aclService.isSuperAdmin())
      return isBelowThreshold;

    const totalRowsInImportedDoc = this.import.sheets.reduce(
      (total, sheet) => total + sheet.rowsCount,
      0,
    );

    // notify user and reset file
    if (totalRowsInImportedDoc > threshold) {
      isBelowThreshold = false;
      this.removeFile();
      this.toastr.error(
        this.translate.instant('ERRORS.IMPORT_THRESHOLD_REACHED', {
          amount: threshold,
          contact: 'Julie CAHEN <julie.cahen@uzai.tech>',
        }),
      );
    }

    return isBelowThreshold;
  }

  async _parseFile(file: File) {
    try {
      this.startLoading();
      // Reset import data
      this.prepareImport();

      // Read excel file
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      // Ensure a sheet is selected
      if (!this.import.sheet) {
        this.import.sheets = workbook.worksheets.map((sheet, index: number) => ({ selected: index === 0, label: sheet.name, value: sheet.name, rowsCount: sheet.actualRowCount }));
        this.import.sheet = this.import.sheets[0]?.value;
      }

      // Get selected worksheet
      const worksheet = workbook.getWorksheet(this.import.sheet);
      if (!worksheet) throw new Error('Worksheet not found');

      // Populate image fields from images
      const mapLineToImgUrl: { [line: number]: string } = {};
      // Beware that images inserted into hyperlinks into formula, like `=IMAGE(...)` are not detected by ExcelJS
      for (const img of worksheet.getImages()) {
        const imgBuffer: ExcelJS.Buffer | undefined = workbook.getImage(Number.parseInt(img.imageId))?.buffer;
        const line = img.range.tl.nativeRow + 1; // ExcelJS rows are 0-indexed
        if (!imgBuffer) {
          console.warn(`Image buffer not found for image id ${img.imageId} at line ${line}`);
          continue;
        }
        const imgObjUrl = `data:${img.type};base64,${arrayBufferToBase64(imgBuffer)}`;
        mapLineToImgUrl[line] = imgObjUrl;
      }

      // Populate string fields from cells
      worksheet.eachRow((row: any, rowNumber: number) => {
        if (rowNumber < Number(this.import.firstLine)) return;
        const tmImportLines: ITrademarkImportLine[] | null = this._parseRow(row, rowNumber);
        if (tmImportLines) {
          for (const tmline of tmImportLines) {
            tmline.REPRODUCTION = mapLineToImgUrl[tmline.line];
          }
          this.import.trademarks.push(...tmImportLines);
        }
      });
      this.import.parsed = true;
      this._populateTable();
    } catch (err) {
      this.toastr.error('ERRORS.GENERIC');
      console.error(err);
    } finally {
      this.endLoading();
    }
  }

  _parseRow(row: ExcelJS.Row, line: number): ITrademarkImportLine[] | null {
    let valid = true;

    const country = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.COUNTRY_OF_DESIGNATION], row);
    const applicationNumber = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.APPLICATION_NUMBER], row);
    const registrationNumber = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.REGISTRATION_NUMBER], row);
    const brandType = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.BRAND_TYPE], row);
    const office = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.OFFICE], row);
    const publicationDate = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.PUBLICATION_DATE], row);
    const applicationDate = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.APPLICATION_DATE], row);
    const registrationDate = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.REGISTRATION_DATE], row);
    const niceClassification = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.NICE_CLASSIFICATION], row);
    const trademarkName = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.TRADEMARK_NAME], row);
    const status = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.STATUS], row);
    const productServices = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.PRODUCTS_SERVICES], row)
    const clientReference = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.CLIENT_REFERENCE], row)
    const description = this.excelService.fromCol(this.import.columns[ETrademarkImportColumn.DESCRIPTION], row)

    const importLine: ITrademarkImportLine = {
      line,
      [ETrademarkImportColumn.APPLICATION_NUMBER]: applicationNumber?.toString(),
      [ETrademarkImportColumn.REGISTRATION_NUMBER]: registrationNumber?.toString(),
      [ETrademarkImportColumn.COUNTRY_OF_DESIGNATION]: country?.toString(),
      [ETrademarkImportColumn.BRAND_TYPE]: brandType?.toString(),
      [ETrademarkImportColumn.OFFICE]: office?.toString(),
      [ETrademarkImportColumn.PUBLICATION_DATE]: publicationDate?.toString(),
      [ETrademarkImportColumn.APPLICATION_DATE]: applicationDate?.toString(),
      [ETrademarkImportColumn.REGISTRATION_DATE]: registrationDate?.toString(),
      [ETrademarkImportColumn.NICE_CLASSIFICATION]: niceClassification?.toString(),
      [ETrademarkImportColumn.TRADEMARK_NAME]: trademarkName?.toString(),
      [ETrademarkImportColumn.STATUS]: status as TrademarkStatus | undefined,
      [ETrademarkImportColumn.PRODUCTS_SERVICES]: productServices?.toString(),
      [ETrademarkImportColumn.CLIENT_REFERENCE]: clientReference?.toString(),
      [ETrademarkImportColumn.DESCRIPTION]: description?.toString(),
    };

    if (!applicationNumber && !registrationNumber) {
      this.import.errors.missingIdentifier.push(importLine);
      valid = false;
    }

    if (!country) {
      this.import.errors.missingCountry.push(importLine);
      valid = false;
    }

    const countries = country ? country.toString().split(',').map((c: string) => c.trim()) : [];
    const tmImportLines: ITrademarkImportLine[] = [];

    for (const c of countries) {
      const importLineOneCountry = { ...importLine, [ETrademarkImportColumn.COUNTRY_OF_DESIGNATION]: c };
      if (!this._validCountry(c)) {
        this.import.errors.invalidCountry.push(importLineOneCountry);
      }
      tmImportLines.push(importLineOneCountry);
    }

    if (!brandType) {
      this.import.errors.missingBrandType.push(importLine);
      valid = false;
    }

    if (!office) {
      this.import.errors.missingOffice.push(importLine);
      valid = false;
    }

    return valid ? tmImportLines : null;
  }

  _validCountry(country: string) {
    return country && this.wipoCodes.includes(country.toString().toLowerCase());
  }

  overview(type: string, text: string) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '1200px',
      maxHeight: '600px',
      autoFocus: false,
      data: {
        title: 'TRADEMARK_DECLARE.IMPORT_PRE_OVERVIEW',
        text: text,
        trademarks: type === 'trademarks' ? this.import.trademarks : this.import.errors[type as keyof TrademarkErrors]
      }
    }
    const dialog = this.dialog.open(TrademarksImportOverviewListDialogComponent, config);
  }

  hasErrors() {
    return this.import.errors.invalidCountry.length > 0 || this.import.errors.missingCountry.length > 0 || this.import.errors.missingIdentifier.length > 0 || this.import.errors.missingBrandType.length > 0 || this.import.errors.missingOffice.length > 0 || this.import.errors.invalidFamily.length > 0 || this.import.errors.unprocessable.length > 0 || this.import.errors.search.length > 0;
  }

  icon(file: File) {
    return this.documentService.icon(file);
  }

  size(file: File) {
    return this.documentService.size(file);
  }

  private rowKey(row: TrademarkDTO) {
    return `${row.countryOfDesignation}_${row.identifierNumber}`;
  }

  private refreshSelectionState() {
    this.selectedKeys = new Set(this.selection.selected.map(row => this.rowKey(row)));
    const selectable = this.dataSource.data.filter(row => !this.isDisabled(row));
    const selectedCount = selectable.filter(row => this.isSelected(row)).length;
    this.allSelected = selectedCount === selectable.length;
    this.someSelected = selectedCount > 0 && selectedCount < selectable.length;
  }

  isSelected(row: TrademarkDTO) {
    return this.selectedKeys.has(this.rowKey(row));
  }

  isDisabled(row: TrademarkDTO) {
    return false;
  }

  isAllSelected() {
    return this.allSelected;
  }

  isSomeSelected() {
    return this.someSelected;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      const difference = _.differenceBy(this.selection.selected, this.dataSource.data.filter(row => !this.isDisabled(row)), x => `${x.countryOfDesignation}_${x.identifierNumber}`)
      this.selection.setSelection(...difference)
      return;
    }
    this.selection.setSelection(...[...this.selection.selected, ...this.dataSource.data.filter(row => !this.isDisabled(row)).filter(row => !this.isSelected(row))])
  }

  toggle(row: TrademarkDTO) {
    if (this.isSelected(row)) {
      this.selection.setSelection(...this.selection.selected.filter(doc => `${doc.countryOfDesignation}_${doc.identifierNumber}` !== `${row.countryOfDesignation}_${row.identifierNumber}`))
    } else {
      this.selection.setSelection(...[...this.selection.selected, row])
    }
  }

  onSortChange(event: any) {

    if (event.active === 'tmName') {
      this.trademarks.sort((a, b) => event.direction === 'desc' ? (b.name || '').localeCompare(a.name || '') : (a.name || '').localeCompare(b.name || ''));
    } else if (event.active === 'identifierNumber') {
      this.trademarks.sort((a, b) => event.direction === 'desc' ? b.identifierNumber.localeCompare(a.identifierNumber) : a.identifierNumber.localeCompare(b.identifierNumber));
    } else if (event.active === 'countryOfDesignation') {
      this.trademarks.sort((a, b) => event.direction === 'desc' ? this.translate.instant(`TERRITORIES.${b.countryOfDesignation.toUpperCase()}`).localeCompare(this.translate.instant(`TERRITORIES.${a.countryOfDesignation.toUpperCase()}`)) : this.translate.instant(`TERRITORIES.${a.countryOfDesignation.toUpperCase()}`).localeCompare(this.translate.instant(`TERRITORIES.${b.countryOfDesignation.toUpperCase()}`)));
    }
    this.dataSource.data = this.trademarks;
    this.refreshSelectionState();
  }

  _trademarkComparator(trademarkA: TrademarkDTOSearch, trademarkB: TrademarkDTOSearch) {
    return trademarkA.identifierNumber === trademarkB.identifierNumber && trademarkA.countryOfDesignation === trademarkB.countryOfDesignation
  }

  async _populateTable() {
    this.trademarksWithoutDesignations =
      this.import.trademarks.map(importedTM => {
        const { details } = this.parseProductAndServices(importedTM.PRODUCTS_SERVICES);
        const dto: TrademarkDTO = {
          name: importedTM.TRADEMARK_NAME,
          identifierNumber: (importedTM.APPLICATION_NUMBER ?? importedTM.REGISTRATION_NUMBER)!,
          registrationNumber: importedTM.REGISTRATION_NUMBER,
          applicationNumber: importedTM.APPLICATION_NUMBER,
          countryOfDesignation: importedTM.COUNTRY_OF_DESIGNATION!,
          kind: importedTM.BRAND_TYPE!,
          // An empty niceClassification may contain spaces and dashes
          niceClassification: this.stringListParse(importedTM.NICE_CLASSIFICATION, nc => {
            const classNumber = Number.parseInt(nc, 10);
            return ({
              classNumber,
              details: (details && !Number.isNaN(classNumber))
                ? details.get(classNumber)
                : undefined,
            })
          }),
          applicationDate: importedTM.APPLICATION_DATE ? moment(importedTM.APPLICATION_DATE) : undefined,
          publicationDate: importedTM.PUBLICATION_DATE ? moment(importedTM.PUBLICATION_DATE) : undefined,
          registrationDate: importedTM.REGISTRATION_DATE ? moment(importedTM.REGISTRATION_DATE) : undefined,
          status: importedTM.STATUS as TrademarkStatus | undefined,
          reproductionURI: importedTM.REPRODUCTION,
          propertyOffice: {
            ref: "",
            code: importedTM.OFFICE!,
            name: ""
          },
          clientReference: importedTM.CLIENT_REFERENCE || undefined,
          description: importedTM.DESCRIPTION || undefined,
        }
        return dto;
      }).filter(tm => tm.identifierNumber && tm.countryOfDesignation);
    this.dataSource.data = this.trademarksWithoutDesignations;
    this.refreshSelectionState();
  }

  _isDesignation(trademarkA: TrademarkDTOSearch, trademarkB: TrademarkDTOSearch) {
    return (trademarkA.type === trademarkB.type)
      && (trademarkA.kind === trademarkB.kind)
      && (trademarkA.status === trademarkB.status)
      && (trademarkA.applicationDate && trademarkB.applicationDate ? moment(trademarkA.applicationDate).isSame(moment(trademarkB.applicationDate)) : true)
      && (trademarkA.countryOfDesignation !== trademarkB.countryOfDesignation)
      && (
        (trademarkA.applicationNumber === trademarkB.applicationNumber)
        || (trademarkA.registrationNumber == trademarkB.registrationNumber)
        || (trademarkA.identifierNumber == trademarkB.identifierNumber)
      )
  }

  public getTrademarksFromSelection() {
    const trademarks: TrademarkDTO[] = [];
    this.selection.selected.forEach((trademark) => {
      if (trademark.countryOfDesignation !== "WO" && trademark.designations && trademark.designations.length > 0) {
        console.log(trademark)
      }
      if (trademark.designation || trademark.countryOfDesignation !== "WO" || !trademark.designations) {
        return trademarks.push(trademark);
      }
      return trademarks.push(...trademark.designations);
    });
    return trademarks;
  }

  onQueryChange(query: any) {
    if (typeof query == 'string') {
      this.dataSource.filter = query;
    }
  }

  /**
   * Returns the default value of a ITrademarkImport
   */
  private initImport(): ITrademarkImport {
    const cols: ITrademarkImportColumns = {
      [ETrademarkImportColumn.REPRODUCTION]: "",
      [ETrademarkImportColumn.TRADEMARK_NAME]: "",
      [ETrademarkImportColumn.OFFICE]: "",
      [ETrademarkImportColumn.COUNTRY_OF_DESIGNATION]: "",
      [ETrademarkImportColumn.APPLICATION_NUMBER]: "",
      [ETrademarkImportColumn.REGISTRATION_NUMBER]: "",
      [ETrademarkImportColumn.STATUS]: "",
      [ETrademarkImportColumn.NICE_CLASSIFICATION]: "",
      [ETrademarkImportColumn.APPLICATION_DATE]: "",
      [ETrademarkImportColumn.BRAND_TYPE]: "",
      [ETrademarkImportColumn.REGISTRATION_DATE]: "",
      [ETrademarkImportColumn.PUBLICATION_DATE]: "",
      [ETrademarkImportColumn.PRODUCTS_SERVICES]: "",
      [ETrademarkImportColumn.CLIENT_REFERENCE]: "",
      [ETrademarkImportColumn.DESCRIPTION]: "",
    }

    return {
      isExcel: false,
      prepared: false,
      parsed: false,
      delimiter: ";",
      sheets: [],
      sheet: undefined,
      firstLine: 1,
      columns: cols,
      trademarks: [],
      familyModes: [
        { selected: true, label: 'TRADEMARK_DECLARE.IMPORT_TRADEMARK_FAMILY_AUTO', value: 'auto' },
        { selected: false, label: 'TRADEMARK_DECLARE.IMPORT_TRADEMARK_FAMILY_SELECT', value: 'select' },
        { selected: false, label: 'TRADEMARK_DECLARE.IMPORT_TRADEMARK_FAMILY_CREATE', value: 'create' },
        { selected: false, label: 'TRADEMARK_DECLARE.IMPORT_TRADEMARK_FAMILY_COLUMN', value: 'column' }
      ],
      familyMode: undefined,
      familyColumn: undefined,
      family: undefined,
      errors: {
        missingCountry: [],
        missingIdentifier: [],
        invalidCountry: [],
        unprocessable: [],
        search: [],
        notFound: [],
        missingBrandType: [],
        missingOffice: [],
        invalidFamily: []
      }
    };

  }

  /**
   * Clears search data and error list
   */
  public prepareImport() {
    this.import.parsed = false;
    this.import.trademarks = [];
    this.import.errors = {
      missingCountry: [],
      missingIdentifier: [],
      invalidCountry: [],
      unprocessable: [],
      search: [],
      notFound: [],
      missingBrandType: [],
      missingOffice: [],
      invalidFamily: []
    };
  }

  private startLoading() {
    this._message.emitChange("LOADING", "START");
    this.loading = true;
  }

  private endLoading() {
    this._message.emitChange("LOADING", "END");
    this.loading = false;
  }

  /**
   * Attempts to parse a string of following pattern:
   *
   * `(06) Class 6 - Common metals and their alloys, ores; metal materials for building and construction. (09) Class 9 - Metal containers for storage or transport; safes. (x) ...`
   *
   * and turns it into this:
   * @example ```js
   * {
   *    6: 'Class 6 - Common metals and their alloys, ores; metal materials for building and construction.',
   *    9: 'Class 9 - Metal containers for storage or transport; safes.',
   *    x: '...',
   * }
   * ```
   */
  private parseProductAndServices(text: string | undefined) {
    if (!text) return { details: null, error: 'Text is undefined' };

    // Normalize newlines and collapse multiple spaces
    const normalizedText = text.replaceAll(/\n|\r\n?/g, '')
      .replaceAll(/[ \t]+/g, ' ');
    const re = /\(\s*(\d+)\s*\)/g; // '(digit)'

    const result: Map<number, string> = new Map();
    let match: RegExpExecArray | null;
    const identifiers: { id: string, index: number, endIndex: number }[] = [];

    // collect identifiers and their start index
    while ((match = re.exec(normalizedText)) !== null) {
      identifiers.push({ id: match[1], index: match.index, endIndex: re.lastIndex });
    }

    if (identifiers.length === 0) return { details: null, error: 'Could not find identifiers for P&S details.' };

    // for each identifier, take text from its endIndex up to next identifier.index
    for (let i = 0; i < identifiers.length; i++) {
      const current = identifiers[i];
      const next = identifiers[i + 1];
      const classId = Number.parseInt(current.id, 10);
      const slice = normalizedText.slice(current.endIndex, next ? next.index : undefined);
      const value = slice.replaceAll(/^\s+|\s+$/g, '');
      // Skip adding identifiers that have no value (empty or only non-word chars)
      if (value.length === 0 || /^\W*$/.test(value)) continue;
      result.set(classId, value);
    }

    return { details: result, error: null };
  }

  /**
   * Converts a comma-separated string into a list of strings
   * @param str
   * @param mapFn Optional function to map each item
   * @returns
   */
  private stringListParse(str: string | undefined, mapFn?: (item: string) => any): any[] {
    // If empty or only spaces/dashes/underscores, return empty list
    if (!str || /^[\s_-]*$/.test(str)) return [];
    const items = str.split(',').map(s => s.trim());
    return mapFn ? items.map(mapFn) : items;
  }
}
